import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { Callout, DefaultButton, PrimaryButton, ChoiceGroup, Dropdown, TextField } from '@fluentui/react';

import { isRadiusUnitMiles } from '../util/radius-unit';

const radiusUnits = [
  { key: 'mi', i18nKey: _TL_('miles') },
  { key: 'km', i18nKey: _TL_('kilometers') },
];

export function LocationTargetEditCallout(props) {
  const {
    className,
    isBoundaryChoiceEnabled,
    isRadiusChoiceEnabled,
    isRadiusLocationTarget: initialIsRadiusLocationTarget,
    locationName,
    maxRadiusInKilometers,
    maxRadiusInMiles,
    onDismiss,
    onSave,
    radiusSize: initialRadiusSize,
    radiusUnit: initialRadiusUnit,
    targetElement,
  } = props;

  const i18n = useContext(I18nContext);

  const [isRadiusLocationTarget, setIsRadiusLocationTarget]
    = useState(initialIsRadiusLocationTarget);
  const [radiusSize, setRadiusSize] = useState(initialRadiusSize);
  const [radiusUnit, setRadiusUnit] = useState(initialRadiusUnit);
  const [isValid, setIsValid] = useState(true);

  const radiusUnitDropdownOptions = useMemo(() => _.map(radiusUnits, option => ({
    ...option,
    text: i18n.getString(option.i18nKey),
    title: i18n.getString(option.i18nKey),
  })), [i18n]);

  const errorMessage = useMemo(() => i18n.getString(
    _TL_('Target radius is invalid. Valid values are positive integers less than {{maxMiles}} miles or {{maxKilometers}} kilometers.'),
    { maxMiles: maxRadiusInMiles, maxKilometers: maxRadiusInKilometers }
  ), [i18n, maxRadiusInKilometers, maxRadiusInMiles]);

  const onRadiusSizeChange = useCallback((_event, newValue) => {
    setRadiusSize(newValue);
  }, []);

  useEffect(() => {
    let newIsValid = false;

    // check if radius size and unit are valid
    if (/^\d+$/.test(radiusSize)) {
      const radiusSizeAsInt = parseInt(radiusSize, 10);

      if (_.isFinite(radiusSizeAsInt) && radiusSizeAsInt > 0) {
        if (isRadiusUnitMiles(radiusUnit)) {
          newIsValid = radiusSizeAsInt <= maxRadiusInMiles;
        } else {
          newIsValid = radiusSizeAsInt <= maxRadiusInKilometers;
        }
      }
    }

    setIsValid(newIsValid);
  }, [radiusSize, radiusUnit, maxRadiusInMiles, maxRadiusInKilometers]);

  const choiceGroupOptions = [
    {
      key: 'Radius',
      'data-automation-id': 'radius-location-choice',
      disabled: !isRadiusChoiceEnabled,
      text: i18n.getString(_TL_('Radius')),
      onRenderField: (fieldProps, render) => (
        <div className="radius-location-choice">
          {render(fieldProps)}
          <TextField
            className="radius-location-size-input"
            value={radiusSize}
            disabled={!fieldProps || !fieldProps.checked}
            onChange={onRadiusSizeChange}
            errorMessage={!isValid && errorMessage}
            type="number"
          />
          <Dropdown
            className="radius-unit-dropdown"
            options={radiusUnitDropdownOptions}
            defaultSelectedKey={radiusUnit}
            onChange={(_event, option) => setRadiusUnit(option.key)}
            disabled={!fieldProps || !fieldProps.checked}
          />

        </div>
      ),
    },
    {
      key: 'Boundary',
      'data-automation-id': 'boundary-location-choice',
      disabled: !isBoundaryChoiceEnabled,
      text: i18n.getString(_TL_('Within {{locationName}}'), { locationName }),
      onRenderField: (fieldProps, render) => (
        <div className="boundary-location-choice">
          {render(fieldProps)}
          {!isBoundaryChoiceEnabled &&
            <p className="boundary-location-choice-disabled">
              {i18n.getString(_TL_('You can’t target a street address. Use a radius around the address instead.'))}
            </p>}
        </div>
      ),
    },
  ];

  const handleSave = () => {
    if (!isValid) {
      return;
    }

    const radiusSizeAsInt = parseInt(radiusSize, 10);
    onSave(
      isRadiusLocationTarget,
      isRadiusLocationTarget ? radiusSizeAsInt : null,
      isRadiusLocationTarget ? radiusUnit : null
    );
  };

  return (
    <Callout
      className={className}
      role="alertdialog"
      target={targetElement}
      onDismiss={onDismiss}
      setInitialFocus
    >
      <FocusLock
        focusFirstItemOnMount
        lockRegionAriaLabel={i18n.getString(_TL_('dialog is ready'))}
      >
        <div>
          <ChoiceGroup
            className="location-choice-group"
            defaultSelectedKey={isRadiusLocationTarget ? 'Radius' : 'Boundary'}
            options={choiceGroupOptions}
            onChange={(_event, option) => setIsRadiusLocationTarget(option.key === 'Radius')}
          />

          {!isValid && <p className="location-callout-error text-danger">{errorMessage}</p>}

          <div className="location-callout-buttons">
            <PrimaryButton
              className="location-callout-save"
              text={i18n.getString(_TL_('Done'))}
              onClick={handleSave}
            />
            <DefaultButton
              className="location-callout-dismiss"
              text={i18n.getString(_TL_('Cancel'))}
              onClick={onDismiss}
            />
          </div>
        </div>
      </FocusLock>
    </Callout>
  );
}

LocationTargetEditCallout.propTypes = {
  className: PropTypes.string.isRequired,
  isBoundaryChoiceEnabled: PropTypes.bool.isRequired,
  isRadiusChoiceEnabled: PropTypes.bool.isRequired,
  isRadiusLocationTarget: PropTypes.bool.isRequired,
  locationName: PropTypes.string.isRequired,
  maxRadiusInKilometers: PropTypes.number,
  maxRadiusInMiles: PropTypes.number,
  onDismiss: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  radiusSize: PropTypes.number.isRequired,
  radiusUnit: PropTypes.oneOf(_.pluck(radiusUnits, 'key')).isRequired,
  targetElement: PropTypes.instanceOf(Element).isRequired,
};

LocationTargetEditCallout.defaultProps = {
  maxRadiusInMiles: 500,
  maxRadiusInKilometers: 800,
};
